import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import PurchaseOrderHeader from "./PurchaseOrderHeader";
import PurchaseOrderItems from "./PurchaseOrderItems";

const PurchaseOrderDetailsPage = ({ setView, context }) => {
  const { user } = useAuth();
  const [PurchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [purchaseOrderItems, setpurchaseOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Accordion states
  const [isPurchaseOrderHeaderOpen, setIsPurchaseOrderHeaderOpen] = useState(false);
  const [ispurchaseOrderItemsOpen, setIspurchaseOrderItemsOpen] = useState(true);

  const fetchOrderData = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      try {
        const orderData = await api.getPurchaseOrderForEdit(context.id);

        if (orderData.Table && orderData.Table.length > 0) {
          setPurchaseOrderDetails({
            ...orderData.Table[0],
            Table2: orderData.Table2 || [],
          });
        }
        setpurchaseOrderItems(orderData.Table1 || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load order data.");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [context.id]
  );

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  if (loading) return <Spinner />;
  if (error) return <MessageBox message={error} />;

  // --- Accordion reusable section ---
  const AccordionSection = ({ title, isOpen, onToggle, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-gray-100 cursor-pointer transition-colors duration-200 ease-in-out hover:bg-gray-200"
        onClick={onToggle}
      >
        <h2 className="text-lg font-medium text-gray-800">{title}</h2>
        <svg
          className={`w-5 h-5 text-gray-600 transform transition-transform duration-300 ease-in-out ${isOpen ? "rotate-180" : "rotate-0"
            }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Content */}
      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? "max-h-none opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <div className="p-4 bg-white">{children}</div>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top title bar */}
      <div className="p-4 bg-white shadow">
        <h1 className="mb-4 text-xl font-bold text-center text-gray-900">
          {`Purchase Order Details #${context.id}`}
        </h1>
      </div>

      <div className="flex justify-center mt-6">
        <div className="w-full max-w-5xl p-4 sm:p-6 bg-white shadow-lg rounded-xl">
          {/* Success message */}
          {success && (
            <MessageBox
              message={success}
              type="success"
              onDismiss={() => setSuccess("")}
            />
          )}

          {/* Accordion for Order Header */}
          {PurchaseOrderDetails && (
            <AccordionSection
              title="Purchase Order Information"
              isOpen={isPurchaseOrderHeaderOpen}
              onToggle={() => setIsPurchaseOrderHeaderOpen(!isPurchaseOrderHeaderOpen)}
            >
              <PurchaseOrderHeader
                orderDetails={PurchaseOrderDetails}   // ✅ changed
                user={user}
                setSuccess={setSuccess}
                setError={setError}
                setOrderDetails={setPurchaseOrderDetails} // ✅ keep consistent
              />

            </AccordionSection>
          )}

          {/* Accordion for Order Items */}
          <AccordionSection
            title={`Purchase Order Items (${PurchaseOrderItems.length})`}
            isOpen={ispurchaseOrderItemsOpen}
            onToggle={() => setIspurchaseOrderItemsOpen(!ispurchaseOrderItemsOpen)}
          >
            <PurchaseOrderItems
              orderId={context.id}
              customerId={context.customerId}
              orderDate={context.orderDate}
              items={purchaseOrderItems}
              user={user}
              setSuccess={setSuccess}
              setError={setError}
              refetchData={() => fetchOrderData(false)}
            />
          </AccordionSection>

          {/* Bottom Actions */}
          <div className="flex justify-end gap-3 pt-4 mt-6 border-t">
            <Button
              type="button"
              onClick={() => setView("purchaseorders")}
              className="bg-pink-500 hover:bg-pink-600 px-4 py-2 text-white text-sm rounded"
            >
              Finish Purchase Order
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDetailsPage;
